import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, SearchIcon, PlusIcon, ListIcon, XIcon, PackageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { RichTextEditor } from '../components/RichTextEditor';
import { Customer } from '../components/CustomerCard';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
interface StandardMaterial {
  id: string;
  name: string;
  price?: number;
}
interface AddTaskPageProps {
  customers: Customer[];
  nextTaskId: string;
  onAdd: (task: {
    taskId: string;
    title: string;
    customerId: string;
    description: string;
    status: 'todo' | 'in-progress' | 'done' | 'planned' | 'pending';
    category: 'Ladeboks' | 'Andet';
    chargerType?: 'Zaptec 1' | 'Zaptec 2' | 'Andet';
    taskType: 'Tilbudsopgave' | 'Regningsopgave';
    agreedPlanning?: string;
    agreedInstallation?: string;
    carDeliveryDate?: string;
    hours?: number;
    materialPrice?: number;
    hourlyRate?: number;
    quotePrice?: number;
    totalPrice?: number;
    invoiced: boolean;
    subTasks?: string;
    materials?: string[];
    workLog?: Array<{
      date: string;
      description: string;
    }>;
  }) => void;
  onBack: () => void;
  initialCustomerId?: string;
}
export function AddTaskPage({
  customers,
  nextTaskId,
  onAdd,
  onBack,
  initialCustomerId
}: AddTaskPageProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState(initialCustomerId || '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [category, setCategory] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [standardMaterials, setStandardMaterials] = useState<StandardMaterial[]>([]);
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  // Load standard materials
  useEffect(() => {
    const docRef = doc(db, 'settings', 'standardMaterials');
    const unsubscribe = onSnapshot(docRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setStandardMaterials(data.items || []);
      }
    }, error => {
      console.error('Error loading standard materials:', error);
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setChargerPrices(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    loadPrices();
  }, []);
  useEffect(() => {
    if (category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, category, chargerPrices]);
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const query = customerSearch.toLowerCase();
    return customers.filter(customer => customer.name.toLowerCase().includes(query) || customer.customerId.toLowerCase().includes(query) || customer.phone.toLowerCase().includes(query) || customer.email?.toLowerCase().includes(query));
  }, [customers, customerSearch]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = hours ? parseFloat(hours) : 0;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      const laborPrice = quote - material;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate,
        laborPrice: quote > 0 ? laborPrice : null
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      return {
        totalPrice: totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate,
        laborPrice: rate > 0 && time > 0 ? laborCost : null
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, hours]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        const newTotalPrice = currentPrice + additionalPrice;
        setMaterialPrice(newTotalPrice.toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddPresetMaterial = (preset: StandardMaterial) => {
    setMaterials([...materials, preset.name]);
    if (preset.price !== undefined) {
      const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
      const newTotalPrice = currentPrice + preset.price;
      setMaterialPrice(newTotalPrice.toString());
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          const additionalPrice = parseFloat(bulkPrice);
          const newTotalPrice = currentPrice + additionalPrice;
          setMaterialPrice(newTotalPrice.toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !customerId) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onAdd({
      taskId: nextTaskId,
      title,
      customerId,
      description,
      status,
      category,
      chargerType: category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined
    });
    onBack();
  };
  return <div className="min-h-screen bg-stone-50 py-8" data-id="element-839" data-mp="2qcbjl45sdqdf">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-840" data-mp="2vcpv4w6ljig7">
        <button onClick={onBack} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors mb-6" data-id="element-841" data-mp="2vav6p7w4987d">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-842" data-mp="18f6ruags86tc" />
          <span className="font-medium" data-id="element-843" data-mp="18icku0sj7suc">Tilbage</span>
        </button>

        <div className="mb-8" data-id="element-844" data-mp="1mfhjvgttvrg3">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-845" data-mp="1mdoowyskuvnx">
            Tilføj ny opgave
          </h1>
          <p className="text-stone-500 mt-2" data-id="element-846" data-mp="uolhja5e9572">
            Opgave-ID:{' '}
            <span className="font-medium text-teal-600" data-id="element-847" data-mp="umh523tfixv6">{nextTaskId}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-id="element-848" data-mp="pnxgdvngb7z4">
          {/* Grundlæggende information */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-849" data-mp="yqgiuenbriku">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-850" data-mp="yolvktokzedl">
              Grundlæggende information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-851" data-mp="6zsl7ckv0vbx">
              <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required data-id="element-852" data-mp="6xg22w3d8exa" />

              <div data-id="element-853" data-mp="35trubmaerfeg">
                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-854" data-mp="35t8ag0176gxs">
                  Kunde <span className="text-red-500" data-id="element-855" data-mp="30uf0uip17a2w">*</span>
                </label>
                <div className="relative mb-2" data-id="element-856" data-mp="2vwwv116b9az4">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-857" data-mp="2vxombzsvkmjl" />
                  <input type="text" placeholder="Søg efter kunde..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-858" data-mp="2r3kdtc3yo6ge" />
                </div>
                <select value={customerId} onChange={e => setCustomerId(e.target.value)} required className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-859" data-mp="nquqzgl7lzkj">
                  <option value="" data-id="element-860" data-mp="4zk6pi9zol1m">Vælg en kunde</option>
                  {filteredCustomers.map(c => <option key={c.id} value={c.id} data-id="element-861" data-mp="9xvgjt0aavju">
                      {c.name} ({c.customerId})
                    </option>)}
                </select>
                {customerSearch && filteredCustomers.length === 0 && <p className="text-sm text-stone-500 mt-1" data-id="element-862" data-mp="3idf0g1f2f1m8">
                    Ingen kunder matcher søgningen
                  </p>}
              </div>
            </div>

            <div className="mt-6" data-id="element-863" data-mp="tka6jlo7q466">
              <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={4} data-id="element-864" data-mp="tj8kyn9klza7" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-865" data-mp="13bwdh5tiph34">
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
              value: 'planned',
              label: 'Planlagt'
            }, {
              value: 'pending',
              label: 'Afventende'
            }, {
              value: 'in-progress',
              label: 'I gang'
            }, {
              value: 'done',
              label: 'Afsluttet'
            }]} data-id="element-866" data-mp="ybiesedt3jhr" />

              <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
              value: 'no',
              label: 'Nej'
            }, {
              value: 'yes',
              label: 'Ja'
            }]} data-id="element-867" data-mp="1ondfqfq6smll" />
            </div>
          </div>

          {/* Datoer */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-868" data-mp="2cgvbz3m8lh6u">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-869" data-mp="306fwvs1yaoir">
              Datoer
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-870" data-mp="3513po33zm535">
              <div data-id="element-871" data-mp="1hcimk7a2xrfe">
                <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} data-id="element-872" data-mp="1h8us95wt90zm" />
                {agreedPlanning && <div className="mt-3" data-id="element-873" data-mp="12eykp98hccdg">
                    <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} data-id="element-874" data-mp="12g1cdjdmmiuf" />
                  </div>}
              </div>

              <div data-id="element-875" data-mp="1lciz1mdmbsjt">
                <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} data-id="element-876" data-mp="1ld2fszrj8aw9" />
                {agreedInstallation && <div className="mt-3" data-id="element-877" data-mp="2m9uewyf72qgb">
                    <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} data-id="element-878" data-mp="2mb5qhkevt2hz" />
                  </div>}
              </div>
            </div>

            {category === 'Ladeboks' && <div className="mt-6" data-id="element-879" data-mp="358gg0dapohk8">
                <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} data-id="element-880" data-mp="39a1uu46t35x2" />
                <p className="text-sm text-stone-500 mt-1.5" data-id="element-881" data-mp="3izlh7jtq9hwo">
                  Angiv hvornår bilen leveres til kunden
                </p>
              </div>}
          </div>

          {/* Pris og fakturering */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-882" data-mp="3sw6pce11n94q">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-883" data-mp="338189c39bkou">
              Pris og fakturering
            </h2>

            <div className="space-y-6 mb-8" data-id="element-884" data-mp="3czyl0n9wykpo">
              <div data-id="element-885" data-mp="3d0qftvvoseew">
                <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-886" data-mp="3d19osn3czsgs">
                  Kategori
                </label>
                <div className="flex p-1 bg-stone-50 rounded-xl border border-stone-200 w-fit" data-id="element-887" data-mp="3m0d7vr2j07va">
                  {(['Ladeboks', 'Andet'] as const).map(cat => <button key={cat} type="button" onClick={() => setCategory(cat)} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${category === cat ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-888" data-mp="3h1afp77n9w4q">
                      {cat === 'Andet' ? 'Alm. Installation' : cat}
                    </button>)}
                </div>
              </div>

              {category === 'Ladeboks' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="space-y-2" data-id="element-889" data-mp="h4k7e1pkoz89">
                  <label className="block text-sm font-medium text-stone-700" data-id="element-890" data-mp="3tjwbjk340zvu">
                    Ladeboks type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-891" data-mp="3floezd9zk0l7">
                    {(['Zaptec 1', 'Zaptec 2', 'Andet'] as const).map(type => <button key={type} type="button" onClick={() => setChargerType(type)} className={`p-4 rounded-xl border text-left transition-all ${chargerType === type ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500' : 'bg-white border-stone-200 hover:border-stone-300'}`} data-id="element-892" data-mp="1du1mt8ecxzn5">
                          <div className="font-medium text-stone-900" data-id="element-893" data-mp="3sx152zjs3ckb">
                            {type}
                          </div>
                          {type !== 'Andet' && <div className="text-sm text-stone-500 mt-1" data-id="element-894" data-mp="3j17isog9c4pc">
                              {chargerPrices[type].toLocaleString('da-DK')} kr
                            </div>}
                        </button>)}
                  </div>
                </motion.div>}

              <div className="pt-4 border-t border-stone-100" data-id="element-895" data-mp="1883bp4iyk846">
                <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-896" data-mp="187btcvedg6vu">
                  Opgavetype
                </label>
                <div className="flex p-1 bg-stone-50 rounded-xl border border-stone-200 w-fit" data-id="element-897" data-mp="1asj7l5c6gft2">
                  {(['Tilbudsopgave', 'Regningsopgave'] as const).map(type => <button key={type} type="button" onClick={() => setTaskType(type)} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${taskType === type ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-898" data-mp="1kikwu0g9o49k">
                        {type}
                      </button>)}
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200" data-id="element-899" data-mp="2d70b2krkqiju">
              <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide mb-6" data-id="element-900" data-mp="2d92x6tplvrja">
                Timer & Pris
              </h3>

              {taskType === 'Tilbudsopgave' ? <div className="space-y-6" data-id="element-901" data-mp="2i47yl3zmf7j1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-902" data-mp="2m7e3a7734qne">
                    <div data-id="element-903" data-mp="2qym96t97htvt">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-904" data-mp="2qychn18ijeo0">
                        Tilbudspris (kr) <span className="text-red-500" data-id="element-905" data-mp="2vwnrvd2pzr6r">*</span>
                      </label>
                      <input type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-906" data-mp="1z883hkbcjnv8" />
                      <p className="text-xs text-stone-500 mt-1.5" data-id="element-907" data-mp="3ooh834is4cny">
                        Den samlede pris kunden skal betale
                      </p>
                    </div>

                    <div data-id="element-908" data-mp="180g6vf1ealu4">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-909" data-mp="182amcdtwdlqt">
                        Materialer pris (kr)
                      </label>
                      <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-910" data-mp="y9lm6f3sruas" />
                      {category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-911" data-mp="crmgh8gapzyf">
                          Udfyldes automatisk baseret på ladeboks type
                        </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-912" data-mp="322sxphn1hx8">
                          Dine udgifter til materialer
                        </p>}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-stone-200" data-id="element-913" data-mp="voxdemegghap">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-914" data-mp="how36gcavxik">
                      <div data-id="element-915" data-mp="lqzqq3c8usk7">
                        <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-916" data-mp="lq7typ6wuosc">
                          Timer brugt
                        </label>
                        <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-917" data-mp="36hi5axdho3lj" />
                        <p className="text-xs text-stone-500 mt-1.5" data-id="element-918" data-mp="2g6y7quqn668a">
                          Bruges til at udregne din reelle timepris
                        </p>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-919" data-mp="2pxsstvs5izs5">
                        <div data-id="element-920" data-mp="2l62fri1txxx1">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-921" data-mp="2l4rd5lk4ew7i">
                            Samlet pris
                          </p>
                          <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-922" data-mp="1shncttvz3622">
                            {calculations.totalPrice.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                        </div>
                        <div className="pt-3 border-t border-stone-100" data-id="element-923" data-mp="19jrc766krbqk">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-924" data-mp="1ei2lnfsyjk89">
                            Pris kun for timer
                          </p>
                          <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-925" data-mp="1ni7jay8cn498">
                            {(calculations.laborPrice ?? 0).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                          <p className="text-xs text-stone-400 mt-1" data-id="element-926" data-mp="3mqplp8roehco">
                            Tilbudspris − Materialer
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> : <div className="space-y-6" data-id="element-927" data-mp="37xxb4vr69wrf">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-928" data-mp="9iml3uiwv019">
                    <div data-id="element-929" data-mp="4jjc5yzt89o0">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-930" data-mp="4j02f5kbcrl2">
                        Timepris (kr) <span className="text-red-500" data-id="element-931" data-mp="3vojh8hhm96ys">*</span>
                      </label>
                      <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-932" data-mp="3ror740ckqsrb" />
                      <p className="text-xs text-stone-500 mt-1.5" data-id="element-933" data-mp="27twjdo0a0i9z">
                        Din pris per time
                      </p>
                    </div>

                    <div data-id="element-934" data-mp="1yyq6y66i31yh">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-935" data-mp="1yxf2s5957s6u">
                        Materialer pris (kr)
                      </label>
                      <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-936" data-mp="1p2dd98aygo3q" />
                      {category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-937" data-mp="1kbztk6fi5iwj">
                          Udfyldes automatisk baseret på ladeboks type
                        </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-938" data-mp="1t5cvfxvjnuur">
                          Dine udgifter til materialer
                        </p>}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-stone-200" data-id="element-939" data-mp="2342cxpjj3dah">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-940" data-mp="1y4pqv70hea8y">
                      <div data-id="element-941" data-mp="35gnbo35ml9em">
                        <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-942" data-mp="35hevyhtrvkpl">
                          Timer brugt <span className="text-red-500" data-id="element-943" data-mp="39h79l8y0wh1t">*</span>
                        </label>
                        <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-944" data-mp="3eh1w70armkh2" />
                        <p className="text-xs text-stone-500 mt-1.5" data-id="element-945" data-mp="30jdge6vk23iv">
                          Antal timer brugt på opgaven
                        </p>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-946" data-mp="27zexd89soayl">
                        <div data-id="element-947" data-mp="1vinkw59ycr9g">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-948" data-mp="1vidvojwqeo4t">
                            Samlet pris
                          </p>
                          <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-949" data-mp="1lkhk5qzvtr66">
                            {calculations.totalPrice.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                        </div>
                        <div className="pt-3 border-t border-stone-100" data-id="element-950" data-mp="12tzxo45n7rt9">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-951" data-mp="12rvhonugz2cm">
                            Pris kun for timer
                          </p>
                          <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-952" data-mp="tttlqmplezw7">
                            {(calculations.laborPrice ?? 0).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                          <p className="text-xs text-stone-400 mt-1" data-id="element-953" data-mp="27800dzdd0rsq">
                            Timepris × Timer
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Summary Results */}
              <div className="mt-8 pt-6 border-t border-stone-200 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-954" data-mp="2b8tmcn78rwiz">
                {calculations.realHourlyRate !== null && <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 shadow-sm" data-id="element-955" data-mp="1jk08ux884xd3">
                    <p className="text-sm font-medium text-blue-800 mb-1" data-id="element-956" data-mp="19eqj4u0hoamx">
                      Reel timepris
                    </p>
                    <p className="text-2xl font-bold text-blue-700" data-id="element-957" data-mp="1e7jonk3s7n02">
                      {calculations.realHourlyRate.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr/t
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Materialer */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-958" data-mp="1i7u4dohdlrs4">
            <div className="flex items-center justify-between mb-6" data-id="element-959" data-mp="1n6ooquuzroec">
              <h2 className="text-xl font-semibold text-stone-900" data-id="element-960" data-mp="1n8rm5zyh4jt3">
                Materialer
              </h2>
              <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-961" data-mp="6urn0r7gcy6w">
                <ListIcon className="w-4 h-4" data-id="element-962" data-mp="29fvnv8h2skup" />
                {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
              </button>
            </div>

            <AnimatePresence mode="wait" data-id="element-963" data-mp="1zpsjyd8f1mhl">
              {bulkMode ? <motion.div key="bulk" initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-964" data-mp="1uq7m664zfk6g">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-965" data-mp="3b465hbfp5pug">
                    <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-966" data-mp="3ioi65dpftszy">
                      📦 Tilføj stor ordre
                    </p>
                    <p className="text-sm text-blue-700" data-id="element-967" data-mp="3nlgxjohpvlug">
                      Skriv ét materiale per linje. Prisen lægges automatisk til
                      materialer pris.
                    </p>
                  </div>

                  <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m&#10;Stikkontakt&#10;Sikring 16A&#10;Kabelkanal 2m&#10;..." rows={6} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-968" data-mp="19bdtzjifgv0" />

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-id="element-969" data-mp="tvmm7wdcym2d">
                    <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-970" data-mp="rh7p62ewbotf">
                      💰 Pris for denne ordre (kr)
                    </label>
                    <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-3 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-971" data-mp="nhyzb8mz8b35" />
                    {bulkPrice && materialPrice && <div className="mt-4 pt-4 border-t border-amber-200" data-id="element-972" data-mp="1f83emsvvq7xi">
                        <div className="flex items-center justify-between text-base" data-id="element-973" data-mp="1u42tcmdhv6bm">
                          <span className="text-amber-700" data-id="element-974" data-mp="1p585v74l0tl6">
                            Nuværende materialer pris:
                          </span>
                          <span className="font-medium text-amber-900" data-id="element-975" data-mp="28scn0cq5f0fc">
                            {parseFloat(materialPrice).toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                            kr
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-base mt-2" data-id="element-976" data-mp="1p28yls7vvfwg">
                          <span className="text-amber-700" data-id="element-977" data-mp="1tusn3k83zvg1">Ordre pris:</span>
                          <span className="font-medium text-amber-900" data-id="element-978" data-mp="1g2vz9sdh47pg">
                            +
                            {parseFloat(bulkPrice).toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                            kr
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-lg mt-3 pt-3 border-t border-amber-300" data-id="element-979" data-mp="rzpmpkh36sxt">
                          <span className="font-semibold text-amber-900" data-id="element-980" data-mp="n6d2rnncrabe">
                            Ny total:
                          </span>
                          <span className="font-bold text-amber-900" data-id="element-981" data-mp="da07ilc5soej">
                            {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                            kr
                          </span>
                        </div>
                      </div>}
                  </div>

                  <div className="flex gap-4" data-id="element-982" data-mp="3lr4r0xs433ln">
                    <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2 py-3" disabled={!bulkInput.trim()} data-id="element-983" data-mp="3gqqizfqiftnm">
                      <PlusIcon className="w-5 h-5" data-id="element-984" data-mp="266c8m8ahcmgv" />
                      Tilføj alle materialer
                      {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                    </Button>
                    <Button type="button" onClick={() => {
                  setBulkInput('');
                  setBulkPrice('');
                  setBulkMode(false);
                }} variant="secondary" className="py-3 px-6" data-id="element-985" data-mp="2fw5h30fbslgw">
                      Annuller
                    </Button>
                  </div>
                </motion.div> : <motion.div key="single" initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-986" data-mp="ziffq1flcbay">
                  {/* Standard Materials */}
                  <div data-id="element-987" data-mp="2ditlu9fq5b2n">
                    <h3 className="text-sm font-medium text-stone-700 mb-3" data-id="element-988" data-mp="2djl6wqbp3qeo">
                      Vælg fra standard
                    </h3>
                    {standardMaterials.length === 0 ? <p className="text-sm text-stone-500 italic" data-id="element-989" data-mp="2nemydstg14kf">
                        Ingen standard materialer endnu — tilføj dem under
                        Information.
                      </p> : <div className="flex flex-wrap gap-2" data-id="element-990" data-mp="a9891rx4cu5d">
                        {standardMaterials.map(preset => <motion.button key={preset.id} type="button" whileTap={{
                    scale: 0.95
                  }} onClick={() => handleAddPresetMaterial(preset)} className="px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-sm font-medium text-stone-700 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700 transition-all flex items-center gap-2" data-id="element-991" data-mp="f8l2ivihwzgx">
                            <PackageIcon className="w-4 h-4" data-id="element-992" data-mp="o8reue2gjtei" />
                            {preset.name}
                            {preset.price !== undefined && <span className="text-stone-500 font-normal" data-id="element-993" data-mp="1bwhajbzvamgj">
                                · {preset.price.toLocaleString('da-DK')} kr
                              </span>}
                          </motion.button>)}
                      </div>}
                  </div>

                  <div className="pt-4 border-t border-stone-100" data-id="element-994" data-mp="26gnz6g6k4ehz">
                    <h3 className="text-sm font-medium text-stone-700 mb-3" data-id="element-995" data-mp="2befpj5rjzsk2">
                      Tilføj manuelt
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-4" data-id="element-996" data-mp="2z5l4lipo6gtb">
                      <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMaterial();
                    }
                  }} placeholder="Skriv materiale navn..." className="flex-1 px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-997" data-mp="343wf7vnsgvcl" />
                      <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMaterial();
                    }
                  }} placeholder="Pris (kr)" className="w-full sm:w-40 px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-998" data-mp="1l45l2zl1sigq" />
                      <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center justify-center gap-2 py-3 px-6" disabled={!materialInput.trim()} data-id="element-999" data-mp="15ikepo3j42hh">
                        <PlusIcon className="w-5 h-5" data-id="element-1000" data-mp="1jh0tm2bjozap" />
                        Tilføj
                      </Button>
                    </div>
                  </div>

                  {materialPriceInput && materialPrice && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="bg-green-50 border border-green-200 rounded-xl p-4" data-id="element-1001" data-mp="vtsqnr1la2bu">
                      <div className="flex items-center justify-between text-base" data-id="element-1002" data-mp="3no3o1ozyzpri">
                        <span className="text-green-800" data-id="element-1003" data-mp="3iqlfhzpv7ck1">
                          Nuværende materialer pris:
                        </span>
                        <span className="font-medium text-green-900" data-id="element-1004" data-mp="3dsa1dcsjefw0">
                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                          kr
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-base mt-2" data-id="element-1005" data-mp="2uz665mzo7uza">
                        <span className="text-green-800" data-id="element-1006" data-mp="2q2fq2znv8zdv">Tilføjelse:</span>
                        <span className="font-medium text-green-900" data-id="element-1007" data-mp="tyhyj1wayruo">
                          +
                          {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                          kr
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-lg mt-3 pt-3 border-t border-green-300" data-id="element-1008" data-mp="f9vc6g89czty">
                        <span className="font-semibold text-green-900" data-id="element-1009" data-mp="1am98i92c27x">
                          Ny total:
                        </span>
                        <span className="font-bold text-green-900" data-id="element-1010" data-mp="6arsba19ogee">
                          {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                          kr
                        </span>
                      </div>
                    </motion.div>}
                </motion.div>}
            </AnimatePresence>

            {materials.length > 0 && <div className="mt-6" data-id="element-1011" data-mp="7ot722qog1bo">
                <h4 className="text-sm font-medium text-stone-700 mb-3" data-id="element-1012" data-mp="7rxgbej01n97">
                  Tilføjede materialer ({materials.length})
                </h4>
                <ul className="space-y-2" data-id="element-1013" data-mp="hoa7a4n5tn09">
                  {materials.map((material, index) => <li key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200" data-id="element-1014" data-mp="cpyzdzgfdmko" data-mpi={index}>
                      <span className="text-sm text-stone-700" data-id="element-1015" data-mp="2ehc4opmb1wh9">{material}</span>
                      <button type="button" onClick={() => handleRemoveMaterial(index)} className="text-stone-400 hover:text-red-500 transition-colors p-1 flex items-center gap-1" data-id="element-1016" data-mp="344xydvboa4bq">
                        <XIcon className="w-4 h-4" data-id="element-1017" data-mp="3i47fcx7pd2gr" />
                        Fjern
                      </button>
                    </li>)}
                </ul>
              </div>}
          </div>

          {/* Noter */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-1018" data-mp="3r3kqrvfe0144">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-1019" data-mp="3vwnehmxhggce">Noter</h2>
            <RichTextEditor label="Interne noter" value={subTasks} onChange={setSubTasks} placeholder="Tilføj interne noter her..." rows={4} data-id="element-1020" data-mp="4puegmefen9g" />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-4 border-t border-stone-200" data-id="element-1021" data-mp="1z5jc492aas0t">
            <Button type="button" variant="secondary" onClick={onBack} className="w-full sm:w-auto py-3 px-6" data-id="element-1022" data-mp="1u5wy3xemt7qy">
              Annuller
            </Button>
            <Button type="submit" variant="primary" className="w-full sm:w-auto py-3 px-8" data-id="element-1023" data-mp="1k9br56j1xmkh">
              Gem opgave
            </Button>
          </div>
        </form>
      </div>
    </div>;
}